import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal, ViewEncapsulation} from "@angular/core"
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop"
import {AccordionStyle, InterfaceDescriptor, isConfigInput, UiStyle} from "@cm/template-nodes"
import {Breakpoints} from "@app/common/models/constants"
import {DefaultMenuComponent} from "@common/components/viewers/configurator/config-menu/default-menu/default-menu.component"
import {IconsAccordionMenuComponent} from "@common/components/viewers/configurator/config-menu/icons-accordion-menu/icons-accordion-menu.component"
import {MobileTabsMenuComponent} from "@common/components/viewers/configurator/config-menu/mobile-tabs-menu/mobile-tabs-menu.component"
import {ConfigMenuService} from "@common/components/viewers/configurator/config-menu/services/config-menu.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-config-menu",
    templateUrl: "./config-menu.component.html",
    styleUrls: ["./config-menu.component.scss"],
    encapsulation: ViewEncapsulation.ShadowDom,
    imports: [IconsAccordionMenuComponent, DefaultMenuComponent, MobileTabsMenuComponent],
})
export class ConfigMenuComponent implements OnInit {
    readonly $interfaceDescriptors = signal<InterfaceDescriptor<unknown, object>[]>([])

    //https://github.com/angular/angular/issues/53981
    //input/output signals currently do not work properly on web components:
    //Do not rename any of the inputs, they are used on the web component and exposed to the customers
    //Angular elements translates the input signals to html attributes, which are strings, so boolean inputs accept string | boolean
    //and are normalized via computed signals below.
    readonly $uiStyleOverride = input<UiStyle>(undefined, {alias: "uiStyleOverride"}) //old name, used by vado and darran //old name, used by vado and darran
    readonly $uiStyle = input<UiStyle>(undefined, {alias: "uiStyle"})
    readonly $accordionStyle = input<AccordionStyle>("autoClose", {alias: "accordionStyle"})
    readonly $_hideInputsSetByTemplate = input<string | boolean>("true", {alias: "hideInputsSetByTemplate"})
    readonly $_externalMenu = input<string | boolean>("false", {alias: "externalMenu"})
    readonly $_useCaptions = input<string | boolean>("false", {alias: "useCaptions"})
    readonly $_minimizeOnMobile = input<string | boolean>("true", {alias: "minimizeOnMobile"})

    readonly $hideInputsSetByTemplate = computed<boolean>(() => this.$_hideInputsSetByTemplate() === "true" || this.$_hideInputsSetByTemplate() === true)
    readonly $externalMenu = computed<boolean>(() => this.$_externalMenu() === "true" || this.$_externalMenu() === true)
    readonly $useCaptions = computed<boolean>(() => this.$_useCaptions() === "true" || this.$_useCaptions() === true)
    readonly $minimizeOnMobile = computed<boolean>(() => this.$_minimizeOnMobile() === "true" || this.$_minimizeOnMobile() === true)

    private readonly configMenuService = inject(ConfigMenuService)
    private readonly destroyRef = inject(DestroyRef)

    private readonly $_serviceUiStyle = toSignal(this.configMenuService.uiStyle$)
    readonly $_uiStyle = computed<UiStyle | undefined>(() => {
        if (this.$uiStyleOverride() !== undefined) return this.$uiStyleOverride()
        if (this.$uiStyle() !== undefined) return this.$uiStyle()
        return this.$_serviceUiStyle()
    })

    readonly $minimized = signal(false)

    readonly $selectedDescriptorIds = this.configMenuService.$selectedDescriptorIds

    constructor() {
        this.configMenuService.interfaceNew$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((interfaceDescriptors) => {
            this.$interfaceDescriptors.set(interfaceDescriptors.filter((desc) => this.showInput(desc)))
        })
    }

    ngOnInit(): void {
        if (this.$minimizeOnMobile() && window.innerWidth < Breakpoints.mobileScreen) {
            this.$minimized.set(true)
        }
    }

    showInput(descriptor: InterfaceDescriptor<unknown, object>): boolean {
        if (isConfigInput(descriptor)) return true
        return this.$hideInputsSetByTemplate() ? !descriptor.props.isSetByTemplate : true
    }
}
